import { MantineProvider } from '@mantine/core'
import '@mantine/core/styles.css'
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings'
import React, { useMemo } from 'react'

import { widgetTheme } from '~/widgets/shared/theme'
import { WidgetProps } from '~/widgets/shared/types'
import { statsigTier } from '~/widgets/shared/utils'
import '~/widgets/shared/widget.css'

interface WidgetWrapperProps extends WidgetProps {
  children: React.ReactNode
}

// Extend Window interface to include Statsig
declare global {
  interface Window {
    Statsig?: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      instance(): any
    }
  }
}

/**
 * Component that initializes a new Statsig client for widgets, for pages that
 * did not load Statsig themselves. Off the marketing site it reports as the
 * development tier.
 */
function NewStatsigProvider({ children }: { children: React.ReactNode }) {
  const { client, isLoading } = useClientAsyncInit(
    'client-wGMa3QiL4TItKTUaUeXLMIkYSV39NrO5S8eia5Apxpb',
    {},
    { environment: { tier: statsigTier(window.location.hostname) } },
  )

  // Wait for client to be ready before rendering children
  if (isLoading) {
    return null
  }

  return <StatsigProvider client={client}>{children}</StatsigProvider>
}

/**
 * Widget-specific Statsig provider that uses existing instance if available,
 * otherwise initializes analytics for embedded widgets
 */
function WidgetStatsigProvider({ children }: { children: React.ReactNode }) {
  // Check if Statsig is already initialized via script tag
  const existingClient = useMemo(() => {
    if (typeof window !== 'undefined' && window.Statsig?.instance) {
      return window.Statsig.instance()
    }
    return null
  }, [])

  // If existing client, use it directly. Otherwise, initialize a new one.
  if (existingClient) {
    return <StatsigProvider client={existingClient}>{children}</StatsigProvider>
  }

  return <NewStatsigProvider>{children}</NewStatsigProvider>
}

/**
 * Common wrapper component that provides Mantine theme, Statsig analytics,
 * and basic styling for all widgets. This ensures consistent theming and
 * analytics tracking across all widgets.
 */
export function WidgetWrapper({
  children,
  width = '100%',
  height = 'auto',
  className = '',
  style = {},
}: WidgetWrapperProps) {
  return (
    <WidgetStatsigProvider>
      <MantineProvider theme={widgetTheme} defaultColorScheme="light">
        <div
          className={`widget-container ${className}`}
          style={{
            width,
            height,
            fontFamily: 'Inter, sans-serif',
            ...style,
          }}
        >
          {children}
        </div>
      </MantineProvider>
    </WidgetStatsigProvider>
  )
}

export default WidgetWrapper
